import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Icon } from "@/components/Icon";
import { Spinner } from "@/components/kit";
import { PublicLayout } from "@/components/PublicLayout";
import { FEATURES } from "@/data/paywall";
import { ResendReceipt } from "@/components/ResendReceipt";
import { toolSource, track } from "@/lib/toolTrack";

export default function Checkout() {
  const { user, loading } = useAuth();
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();

  if (loading) return <Spinner className="min-h-screen items-center" />;
  if (user) return <Navigate to={user.has_paid ? "/" : "/paywall"} replace />;

  const pay = async () => {
    setBusy(true);
    try {
      const origin_url = window.location.origin;
      const src = toolSource();
      if (src) track(src, "checkout");
      const { data } = await api.post("/payment/create-checkout", { origin_url, device_id: `guest-${crypto.randomUUID()}` });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e, "Could not start checkout"));
      setBusy(false);
    }
  };

  return (
    <PublicLayout>
      <section className="max-w-lg mx-auto px-5 pt-12 mm-fade-up">
        <div className="mm-card p-8 sm:p-10" data-testid="checkout-card">
          {params.get("cancelled") && (
            <div className="mm-soft px-4 py-3 mb-5 text-sm" role="status" data-testid="checkout-cancelled-notice">Payment cancelled. You can try again anytime.</div>
          )}
          <div className="text-xs font-semibold text-mm-primary uppercase tracking-wider">Step 1 of 2 · Pay</div>
          <h1 className="text-3xl font-bold mt-2">Unlock Mindful Me</h1>
          <p className="text-mm-muted mt-1">Pay once, then create your account. Lifetime access.</p>
          <ul className="grid sm:grid-cols-2 gap-3 mt-6">
            {FEATURES.map((f) => (
              <li key={f.text} className="flex items-center gap-2 text-sm"><span style={{ color: f.color }}><Icon name={f.icon} size={18} /></span>{f.text}</li>
            ))}
          </ul>
          <div className="mm-soft p-5 mt-8 flex items-baseline gap-2">
            <span className="font-display text-4xl font-extrabold" data-testid="checkout-price">$19.99</span>
            <span className="text-mm-muted text-sm">one-time · lifetime</span>
          </div>
          <button className="mm-btn w-full mt-6" onClick={pay} disabled={busy} data-testid="checkout-paypal-button">
            {busy && <Loader2 size={16} className="animate-spin" />}
            Pay with PayPal
          </button>
          <p className="text-xs text-mm-muted text-center mt-3">Pay with your PayPal balance or a card through PayPal.</p>
          <Link to="/privacy-promise" className="flex items-center justify-center gap-1.5 text-xs text-mm-primary font-semibold mt-3" data-testid="checkout-privacy-promise-link"><ShieldCheck size={14} /> Your journal stays private. Read our promise</Link>
          <p className="text-center text-sm text-mm-muted mt-6">
            Already have an account? <Link to="/login" className="text-mm-primary font-semibold" data-testid="checkout-login-link">Log in</Link>
          </p>
          <div className="text-center mt-3"><ResendReceipt /></div>
        </div>
      </section>
    </PublicLayout>
  );
}
